import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import i18next from 'i18next';
import { ApiError } from '../../api/generated/core/ApiError';
import { MarketplaceListingService } from '../../api/generated/services/MarketplaceListingService';
import type { MarketplaceListingSubmitResultView } from '../../api/generated/models/MarketplaceListingSubmitResultView';
import type { MarketplaceListingGenerateSyncView } from '../../api/generated/models/MarketplaceListingGenerateSyncView';
import type { MarketplaceSyncStatusView } from '../../api/generated/models/MarketplaceSyncStatusView';
import type { MarketplaceMetadataRefreshResultView } from '../../api/generated/models/MarketplaceMetadataRefreshResultView';
import type { MarketplaceCode } from '../../utils/marketplaceListing';

/** Allowed metadata refresh types (`cType`) - derived from the generated client. */
export type MarketplaceRefreshType = NonNullable<
  Parameters<typeof MarketplaceListingService.postAppMarketplaceListingRefresh>[0]['types']
>[number];

/**
 * Body shape for `POST /v2/api/marketplace-listing/generate` - same product
 * addressing as Content Master bulk generate (`items: [{ wawiArtikelId }]`)
 * plus the marketplace channel `source` (`shop:N`) and the generation language.
 * There is no batch entity: the response only reports accepted / skipped ids.
 *
 * `generateRequired/Recommended/Other` are the field-group toggles ("which
 * attributes to generate"). When omitted the backend defaults to
 * required=true, recommended=false, other=false.
 */
export type MarketplaceListingGenerateSubmit = {
  source: string;
  language: string;
  items: Array<{ wawiArtikelId: number }>;
  /** Override; omit to auto-detect from the channel. */
  marketplace?: MarketplaceCode | null;
  generateRequired?: boolean;
  generateRecommended?: boolean;
  generateOther?: boolean;
  /**
   * Also generate attributes Wawi already pre-fills for the channel/category
   * ("Verkaufskanalfelder zuordnen"). Default false - the backend skips and
   * does not bill them; a generated value overrides the Wawi default on apply.
   */
  generatePrefilled?: boolean;
  /**
   * "Fill only empty fields": attributes already filled in Wawi for this
   * channel and an existing channel description are left untouched; a product
   * with nothing left ends as listing_generate_done without an AI call.
   */
  onlyEmptyFields?: boolean;
  /**
   * Request-level flag: AI-detect + assign a category for every product in the
   * request that has none, before generating (backend ticket §4).
   */
  autoDetectCategory?: boolean;
  dataSourceId?: string | null;
};

/**
 * Body for `POST /v2/api/marketplace-listing/generate-sync` - the synchronous
 * single-product counterpart of `/generate`. Same field-group toggles, but ONE
 * `wawiArtikelId` (not an array): generation runs inside the request and the
 * response already carries the final status/summary.
 */
export type MarketplaceListingGenerateSyncSubmit = {
  source: string;
  language: string;
  wawiArtikelId: number;
  /** Override; omit to auto-detect from the channel. */
  marketplace?: MarketplaceCode | null;
  generateRequired?: boolean;
  generateRecommended?: boolean;
  generateOther?: boolean;
  /**
   * Also generate attributes Wawi already pre-fills for the channel/category
   * ("Verkaufskanalfelder zuordnen"). Default false - the backend skips and
   * does not bill them; a generated value overrides the Wawi default on apply.
   */
  generatePrefilled?: boolean;
  /** "Fill only empty fields". Whole-product mode only - the backend ignores it with `fields`. */
  onlyEmptyFields?: boolean;
  autoDetectCategory?: boolean;
  dataSourceId?: string | null;
  /**
   * User-provided extra product info (free text + client-parsed files), merged
   * into ONE string ≤50000 chars. Single generation only - never stored;
   * omitted when empty.
   */
  additionalContext?: string | null;
  /**
   * "Regenerate one field": explicit list of field keys (`mpAttr_*` of the
   * selected category and/or `"description"`). When set, the backend IGNORES
   * the generate* group flags and the prefilled skip - so the caller must not
   * send them together with `fields`. generate-sync only (never bulk).
   */
  fields?: string[] | null;
};

/**
 * Structured reject payload for both listing generate thunks so the UI can
 * branch on the API `errorCode`: the 403 pre-flight rejections
 * (`content_master_quota_exceeded` / `content_master_subscription_insufficient`)
 * open the shared Content Master modals, `already_in_progress` (409) and the
 * 400 codes fall back to a toast with the message.
 */
export type MarketplaceListingGenerateSyncError = {
  errorCode: string | null;
  message: string;
};

/**
 * Listing 403 `errorCode`s handled via the shared Content Master modals on the
 * page level (the slice keeps no `generateError` for them so the toast effect
 * stays silent). The modal actions live in `contentMasterSlice`, which imports
 * from this module - dispatching them here would create an import cycle.
 */
const isMarketplaceListingForbiddenErrorCode = (
  errorCode: string | null | undefined
): boolean =>
  errorCode === 'content_master_quota_exceeded' ||
  errorCode === 'content_master_subscription_insufficient';

interface MarketplaceListingState {
  /** Sync freshness for the channel currently in scope (cleared on source change). */
  syncStatus: MarketplaceSyncStatusView | null;
  syncStatusLoading: boolean;
  syncStatusError: string | null;
  /** In-flight flag for `POST /marketplace-listing/generate`. */
  generateSubmitting: boolean;
  generateError: string | null;
  /** In-flight flag + last error for `POST /marketplace-listing/generate-sync`. */
  generateSyncSubmitting: boolean;
  generateSyncError: MarketplaceListingGenerateSyncError | null;
  /** Last submit result (accepted / skipped) - drives the post-submit toast. */
  lastSubmitResult: MarketplaceListingSubmitResultView | null;
  /** In-flight flag for `POST /marketplace-listing/refresh`. */
  refreshSubmitting: boolean;
  refreshError: string | null;
}

const initialState: MarketplaceListingState = {
  syncStatus: null,
  syncStatusLoading: false,
  syncStatusError: null,
  generateSubmitting: false,
  generateError: null,
  generateSyncSubmitting: false,
  generateSyncError: null,
  lastSubmitResult: null,
  refreshSubmitting: false,
  refreshError: null,
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    const body = error.body as { error?: string; errorCode?: string | null } | undefined;
    const detail = typeof body?.error === 'string' ? body.error : '';
    const code = body?.errorCode;
    // Content Master error codes (incl. `content_master_wawi_error` /
    // `content_master_wawi_connection_failed` returned by the listing submits)
    // reuse the localized `cm_error.<code>` texts; unknown codes fall through
    // to the raw backend message.
    if (typeof code === 'string' && code.startsWith('content_master_')) {
      const translated = i18next.t(`cm_error.${code}`, {
        detail,
        defaultValue: detail || error.message,
      });
      if (typeof translated === 'string' && translated.length > 0) {
        return translated;
      }
    }
    if (detail.length > 0) {
      return detail;
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return i18next.t('error_unexpected', { defaultValue: 'Unknown error' });
};

const toGenerateError = (error: unknown): MarketplaceListingGenerateSyncError => ({
  errorCode:
    error instanceof ApiError
      ? (error.body as { errorCode?: string | null } | undefined)?.errorCode ?? null
      : null,
  message: getErrorMessage(error),
});

/**
 * `GET /v2/api/marketplace-listing/sync-status` - freshness indicator shown
 * before launching generation. Read-only; never blocks the flow.
 */
export const fetchMarketplaceSyncStatus = createAsyncThunk<
  MarketplaceSyncStatusView,
  { source: string; wawiArtikelId?: number; kCategory?: number; dataSourceId?: string | null },
  { rejectValue: string }
>('marketplaceListing/fetchSyncStatus', async (params, { rejectWithValue }) => {
  try {
    return await MarketplaceListingService.getAppMarketplaceListingSyncStatus(
      params.source,
      params.wawiArtikelId,
      params.kCategory,
      params.dataSourceId?.trim() || undefined
    );
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * `POST /v2/api/marketplace-listing/generate` - queues the selected products
 * for asynchronous listing-field generation. The products transition into
 * `listing_generate_queued`; progress is followed on the listing review page
 * (per-status lists from `GET /content-master/products/local`).
 *
 * Rejects with `{ errorCode, message }` so the caller can route the 403
 * pre-flight rejections (quota / subscription) to the shared modals.
 */
export const submitMarketplaceListingGenerate = createAsyncThunk<
  MarketplaceListingSubmitResultView,
  MarketplaceListingGenerateSubmit,
  { rejectValue: MarketplaceListingGenerateSyncError }
>('marketplaceListing/submitGenerate', async (params, { rejectWithValue }) => {
  try {
    return await MarketplaceListingService.postAppMarketplaceListingGenerate({
      source: params.source,
      language: params.language,
      items: params.items,
      marketplace: params.marketplace ?? undefined,
      generateRequired: params.generateRequired,
      generateRecommended: params.generateRecommended,
      generateOther: params.generateOther,
      generatePrefilled: params.generatePrefilled ?? undefined,
      onlyEmptyFields: params.onlyEmptyFields || undefined,
      autoDetectCategory: params.autoDetectCategory,
      dataSourceId: params.dataSourceId?.trim() || undefined,
    });
  } catch (error) {
    return rejectWithValue(toGenerateError(error));
  }
});

/**
 * `POST /v2/api/marketplace-listing/generate-sync` - generates ONE product's
 * listing fields synchronously (no queue). Resolves with the final
 * `listing_generate_done` / `listing_generate_failed` status + summary; rejects
 * with `{ errorCode, message }` so the caller can tell `already_in_progress`
 * (409) / `invalid_request` (400) apart. Live OpenAI call - may take 30s+; the
 * fetch client sets no timeout, so it waits for the response.
 */
export const submitMarketplaceListingGenerateSync = createAsyncThunk<
  MarketplaceListingGenerateSyncView,
  MarketplaceListingGenerateSyncSubmit,
  { rejectValue: MarketplaceListingGenerateSyncError }
>('marketplaceListing/submitGenerateSync', async (params, { rejectWithValue }) => {
  try {
    return await MarketplaceListingService.postAppMarketplaceListingGenerateSync({
      source: params.source,
      language: params.language,
      wawiArtikelId: params.wawiArtikelId,
      marketplace: params.marketplace ?? undefined,
      generateRequired: params.generateRequired,
      generateRecommended: params.generateRecommended,
      generateOther: params.generateOther,
      generatePrefilled: params.generatePrefilled ?? undefined,
      onlyEmptyFields: params.onlyEmptyFields || undefined,
      autoDetectCategory: params.autoDetectCategory,
      dataSourceId: params.dataSourceId?.trim() || undefined,
      additionalContext: params.additionalContext ?? undefined,
      fields: params.fields ?? undefined,
    });
  } catch (error) {
    return rejectWithValue(toGenerateError(error));
  }
});

/**
 * `POST /v2/api/marketplace-listing/refresh` - enqueues a JTL-Worker metadata
 * refresh for the channel. `types` accepts any subset of the client types
 * (`categories` / `channel_attributes` / `category_attributes`); omitting it
 * queues ALL of them. T7: with `category_attributes` the response additionally
 * carries `directSchemas` - missing/stale schemas of the channel's ASSIGNED
 * categories are fetched from the marketplace directly within this request
 * (already done on return; `fetched > 0` → the fields can be re-requested
 * immediately). Everything else is only queued; poll
 * `sync-status.pendingRefresh` for completion.
 */
export const refreshMarketplaceMetadata = createAsyncThunk<
  MarketplaceMetadataRefreshResultView,
  { source: string; types?: MarketplaceRefreshType[] | null; dataSourceId?: string | null },
  { rejectValue: string }
>('marketplaceListing/refresh', async (params, { rejectWithValue }) => {
  try {
    return await MarketplaceListingService.postAppMarketplaceListingRefresh({
      source: params.source,
      types: params.types ?? undefined,
      dataSourceId: params.dataSourceId?.trim() || undefined,
    });
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const marketplaceListingSlice = createSlice({
  name: 'marketplaceListing',
  initialState,
  reducers: {
    clearMarketplaceSyncStatus: (state) => {
      state.syncStatus = null;
      state.syncStatusError = null;
    },
    clearMarketplaceSubmitResult: (state) => {
      state.lastSubmitResult = null;
      state.generateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMarketplaceSyncStatus.pending, (state) => {
        state.syncStatusLoading = true;
        state.syncStatusError = null;
      })
      .addCase(fetchMarketplaceSyncStatus.fulfilled, (state, action) => {
        state.syncStatusLoading = false;
        state.syncStatus = action.payload;
      })
      .addCase(fetchMarketplaceSyncStatus.rejected, (state, action) => {
        state.syncStatusLoading = false;
        state.syncStatus = null;
        state.syncStatusError = action.payload ?? 'Failed to load sync status';
      })
      .addCase(submitMarketplaceListingGenerate.pending, (state) => {
        state.generateSubmitting = true;
        state.generateError = null;
        state.lastSubmitResult = null;
      })
      .addCase(submitMarketplaceListingGenerate.fulfilled, (state, action) => {
        state.generateSubmitting = false;
        state.lastSubmitResult = action.payload;
      })
      .addCase(submitMarketplaceListingGenerate.rejected, (state, action) => {
        state.generateSubmitting = false;
        // Quota / subscription 403s are surfaced via the shared Content Master
        // modals by the caller - keep `generateError` empty so the toast effect
        // stays silent for them.
        state.generateError = isMarketplaceListingForbiddenErrorCode(action.payload?.errorCode)
          ? null
          : action.payload?.message ?? 'Failed to submit listing generation';
      })
      .addCase(submitMarketplaceListingGenerateSync.pending, (state) => {
        state.generateSyncSubmitting = true;
        state.generateSyncError = null;
      })
      .addCase(submitMarketplaceListingGenerateSync.fulfilled, (state) => {
        state.generateSyncSubmitting = false;
      })
      .addCase(submitMarketplaceListingGenerateSync.rejected, (state, action) => {
        state.generateSyncSubmitting = false;
        state.generateSyncError =
          action.payload ?? { errorCode: null, message: 'Failed to generate listing fields' };
      })
      .addCase(refreshMarketplaceMetadata.pending, (state) => {
        state.refreshSubmitting = true;
        state.refreshError = null;
      })
      .addCase(refreshMarketplaceMetadata.fulfilled, (state) => {
        state.refreshSubmitting = false;
      })
      .addCase(refreshMarketplaceMetadata.rejected, (state, action) => {
        state.refreshSubmitting = false;
        state.refreshError = action.payload ?? 'Failed to queue metadata refresh';
      });
  },
});

export const { clearMarketplaceSyncStatus, clearMarketplaceSubmitResult } =
  marketplaceListingSlice.actions;

export default marketplaceListingSlice.reducer;
